import moment from 'moment';

export const FREQUENCIES = [
  {value: 'DAILY', label: 'Daily', hint: 'Covers the previous day (GMT).'},
  {
    value: 'WEEKLY',
    label: 'Weekly',
    hint: 'Covers the last full Monday–Sunday week (GMT).',
  },
  {
    value: 'MONTHLY',
    label: 'Monthly',
    hint: 'Covers the previous calendar month (GMT).',
  },
];

export const DAYS_OF_WEEK = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

// In digest order, which is also the order the API returns them in.
export const SECTIONS = [
  {
    value: 'CHECKOUT_ABANDONMENT',
    label: 'Checkout abandonment',
    description: 'Totals by flow, top reasons, platform and app version',
  },
  {
    value: 'NPS',
    label: 'NPS',
    description: 'Score, promoters / passives / detractors, comments',
  },
  {
    value: 'IN_APP_REVIEW',
    label: 'In-app review',
    description: 'Yes vs Not really, top reasons, comments',
  },
  {
    value: 'MINI_FEEDBACK',
    label: 'Mini feedback',
    description: 'Responses per feature',
  },
  {
    value: 'SUPPORT_CSAT',
    label: 'Support CSAT',
    description: 'Not connected yet; shows “Unavailable”',
    comingSoon: true,
  },
];

// One-line summary of when the digest goes out, e.g. "Every Monday at 08:00 GMT".
export const describeSchedule = values => {
  const at = `at ${formatHour(values.hourGmt)}`;
  if (values.frequency === 'DAILY') return `Every day ${at}`;
  if (values.frequency === 'MONTHLY') {
    return `On day ${values.dayOfMonth} of every month ${at}`;
  }
  return `Every ${formatDayOfWeek(values.dayOfWeek || 'MONDAY')} ${at}`;
};

export const MAX_RECIPIENTS = 20;
const STAFF_EMAIL = /^[^\s@]+@(joindaba\.com|dabafinance\.com)$/i;
const SLACK_CHANNEL_ID = /^[A-Z0-9]{8,20}$/;

// Same rules and messages as the API, so admins see them before saving.
export const validateSlackChannelId = value => {
  const id = (value || '').trim();
  if (id && !SLACK_CHANNEL_ID.test(id)) {
    return 'slackChannelId must be a Slack channel id, e.g. C0123456789';
  }
  return null;
};

export const validateEmailRecipients = (emails = []) => {
  if (emails.length > MAX_RECIPIENTS) return 'At most 20 email recipients';
  const bad = emails.find(email => !STAFF_EMAIL.test(email.trim()));
  return bad ? `${bad} is not a Daba staff email address` : null;
};

export const validateSections = (sections = []) =>
  sections.length ? null : 'Choose at least one section';

export const formatDayOfWeek = day =>
  day.charAt(0) + day.slice(1).toLowerCase();

export const formatHour = hour => `${String(hour).padStart(2, '0')}:00 GMT`;

// Saved settings → form values. Support CSAT is not connected yet, so it
// starts unticked until an admin has saved the settings once.
export const toFormValues = settings => ({
  enabled: settings.enabled,
  frequency: settings.frequency,
  dayOfWeek: settings.dayOfWeek,
  dayOfMonth: settings.dayOfMonth,
  hourGmt: settings.hourGmt,
  slackChannelId: settings.slackChannelId || '',
  emailRecipients: settings.emailRecipients || [],
  sections: settings.updatedAt
    ? settings.sections || []
    : (settings.sections || []).filter(s => s !== 'SUPPORT_CSAT'),
  commentsPerSection: settings.commentsPerSection,
});

// Form values → FeedbackDigestSettingsInput. An empty Slack id clears it.
export const toInput = values => ({
  enabled: values.enabled,
  frequency: values.frequency,
  dayOfWeek: values.dayOfWeek,
  dayOfMonth: values.dayOfMonth,
  hourGmt: values.hourGmt,
  slackChannelId: (values.slackChannelId || '').trim(),
  emailRecipients: (values.emailRecipients || []).map(e =>
    e.trim().toLowerCase(),
  ),
  sections: SECTIONS.map(s => s.value).filter(s =>
    (values.sections || []).includes(s),
  ),
  commentsPerSection: values.commentsPerSection,
});

export const isSameSettings = (a, b) =>
  JSON.stringify(toInput(a)) === JSON.stringify(toInput(b));

export const hasNoDestination = values =>
  !(values?.slackChannelId || '').trim() && !values?.emailRecipients?.length;

const formatPeriod = ({frequency, periodStart}) => {
  const start = moment.utc(periodStart);
  if (frequency === 'MONTHLY') return start.format('MMMM YYYY');
  if (frequency === 'WEEKLY') return `week of ${start.format('D MMM YYYY')}`;
  return start.format('D MMM YYYY');
};

export const describeLastRun = lastRun => {
  if (!lastRun) return 'Last digest: none sent yet';
  const period = formatPeriod(lastRun);
  if (lastRun.status === 'SENT') {
    const finished = lastRun.finishedAt
      ? moment(lastRun.finishedAt).format('D MMM YYYY, HH:mm')
      : '—';
    return `Last digest: ${period} — sent ${finished}`;
  }
  if (lastRun.status === 'SENDING') return `Last digest: ${period} — sending…`;
  if (lastRun.status === 'FAILED') {
    const n = lastRun.attempts ?? 0;
    return `Last digest: ${period} — failed after ${n} attempt${
      n === 1 ? '' : 's'
    }`;
  }
  return `Last digest: ${period} — ${lastRun.status || '—'}`;
};
